<template>
  <div class="main-content row mx-0">
    <div class="col-12 d-flex flex-row-reverse mb-2">
      <refresh-information />
    </div>
    <div :class="[$breakpoint.lgAndUp ? 'col-12 col-md-8' : 'col-12']">
      <div class="panel-tabs">
        <div class="nav-tabs_parent">
          <ul class="nav nav-tabs nav-fill order-capture-tabs">
            <li
              class="nav-item"
              :class="{ active: selectedNav.id === nav.id }"
              v-for="(nav, idx) in navigation"
              :key="idx"
            >
              <a class="nav-link" @click="selectNavItem(nav.id)" href="javascript:void(0);"> {{ nav.title }} </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="card col-12" :class="{ 'pt-4': $breakpoint.smAndDown }">
      <div class="pt-3">
        <p>
          {{
            selectedNav.descriptionBeforeCalcs && selectedNav.descriptionBeforeCalcs.length > 0
              ? selectedNav.descriptionBeforeCalcs.join(" ")
              : ""
          }}
          <button @click="toggleDisplayCalculations()" class="btn btn-link font-style-italic">
            <span v-if="!displayCalculations"> See calculation(s) </span>
            <span v-else> Hide calculation(s) </span>
          </button>
        </p>
      </div>
      <div v-if="displayCalculations">
        <div v-for="(calcs, index) in selectedNav.calculations" :key="'B' + index">
          <p class="font-weight-bold">{{ calcs }}</p>
        </div>
      </div>
      <div v-for="(desc, index) in selectedNav.descriptionAfterCalcs" :key="'C' + index">
        <p>{{ desc }}</p>
      </div>

      <!-- GLOBAL PRICING RULES -->
      <div v-if="selectedNav.id === 'GLOBAL'">
        <div class="fade show" role="tabpanel">
          <settlement-rules
            :rules="componentMetadata.settlement.rules"
            :baseProducts="componentMetadata.settlement.baseProducts"
            :componentKey="componentMetadata.settlement.key"
            @errorFound="preventFormCompletion"
          />
          <market-data
            :rules="componentMetadata.market.rules"
            :componentKey="componentMetadata.market.key"
            @assignIceData="assignIceData"
            @errorFound="preventFormCompletion"
          />
          <product-rules
            :rules="componentMetadata.product.rules"
            :products="componentMetadata.product.products"
            :componentKey="componentMetadata.product.key"
            @errorFound="preventFormCompletion"
          />
        </div>
      </div>

      <!-- COST ADDITIONS -->
      <div v-else-if="selectedNav.id === 'COST_ADDITIONS'">
        <cost-additions
          :enableRuleFilters="true"
          :key="selectedNav.id + renderKey"
          :scope="selectedNav.scope"
          :pendingModificationRuleId.sync="pendingModificationRuleId"
          :pricingRules.sync="componentMetadata.costAdditionsGlobal.rules"
          :priority="rulesEnum.ePriority.Global"
          :originalPricingRulesList="originalPricingRulesList"
          @emit-refresh-render-key="updateRenderKey"
          @newLimitOrPageSelected="newLimitOrPageSelected"
          @updateCostAdditionRulesPricingEngine="populateCostAdditionRulesPaged"
          @setChangesDetected="setChangesDetected"
          @showRemovalConfirmation="showRemovalConfirmation"
        />
      </div>

      <!-- BIO RULES -->
      <div v-else-if="selectedNav.id === 'BIO_COST_ADDITIONS'">
        <cost-additions
          :enableRuleFilters="true"
          :key="selectedNav.id + renderKey"
          :scope="selectedNav.scope"
          :pendingModificationRuleId.sync="pendingModificationRuleId"
          :pricingRules.sync="componentMetadata.costAdditionsBio.rules"
          :priority="rulesEnum.ePriority.Global"
          :originalPricingRulesList="originalPricingRulesList"
          @emit-refresh-render-key="updateRenderKey"
          @newLimitOrPageSelected="newLimitOrPageSelected"
          @updateCostAdditionRulesPricingEngine="populateCostAdditionRulesPaged"
          @setChangesDetected="setChangesDetected"
          @showRemovalConfirmation="showRemovalConfirmation"
        />
      </div>

      <!-- TEMPERATURES -->
      <div v-else-if="selectedNav.id === 'TEMPERATURE'">
        <cost-additions
          :enableRuleFilters="true"
          :key="selectedNav.id + renderKey"
          :scope="selectedNav.scope"
          :pendingModificationRuleId.sync="pendingModificationRuleId"
          :pricingRules.sync="componentMetadata.costAdditionsTemperature.rules"
          :priority="rulesEnum.ePriority.Global"
          :originalPricingRulesList="originalPricingRulesList"
          @emit-refresh-render-key="updateRenderKey"
          @newLimitOrPageSelected="newLimitOrPageSelected"
          @updateCostAdditionRulesPricingEngine="populateCostAdditionRulesPaged"
          @setChangesDetected="setChangesDetected"
          @showRemovalConfirmation="showRemovalConfirmation"
        />
      </div>

      <!-- CUSTOMER MARGIN -->
      <div v-else-if="selectedNav.id === 'CUSTOMER'">
        <cost-additions
          :enableRuleFilters="true"
          :key="selectedNav.id + renderKey"
          :scope="selectedNav.scope"
          :pendingModificationRuleId.sync="pendingModificationRuleId"
          :pricingRules.sync="componentMetadata.costAdditionsCustomer.rules"
          :priority="rulesEnum.ePriority.Global"
          :originalPricingRulesList="originalPricingRulesList"
          @emit-refresh-render-key="updateRenderKey"
          @newLimitOrPageSelected="newLimitOrPageSelected"
          @updateCostAdditionRulesPricingEngine="populateCostAdditionRulesPaged"
          @setChangesDetected="setChangesDetected"
          @showRemovalConfirmation="showRemovalConfirmation"
        />
      </div>

      <div v-else class="mt-3 w-100 alert tables-alert-custom-message-notify text-center">
        <span class="spinner-border spinner-border-sm mr-2 mb-1" role="status"></span> Loading data...
      </div>
    </div>
    <market-prices-calculation
      v-if="selectedNav.id === 'GLOBAL' && hasLivePrices"
      class="w-100 position-sticky top-shadow"
      :class="[{ 'h-50 overflow-auto': $breakpoint.mdAndDown }, { 'h-25 fixed-bottom': !$breakpoint.mdAndDown }]"
      :products="componentMetadata.calculation.products"
      :settlementRules="componentMetadata.settlement.rules"
      :productRules="componentMetadata.product.rules"
      :iceReferences="componentMetadata.calculation.ice"
      :isFormCompletionPrevented="isFormCompletionPrevented"
      :savingStatus="componentMetadata.calculation.savingStatus"
      @saveGlobalPricingRules="saveGlobalPricingRules"
      @updateGlobalRulesSavingStatus="updateGlobalRulesSavingStatus"
    />
  </div>
</template>

<script>
const deepEqual = require("fast-deep-equal");
import breakpoint from "@/mixins/index";
import { mapGetters, mapActions } from "vuex";
import { cloneDeep, isEmpty } from "lodash";

import RefreshInformation from "@/components/RefreshInformation.vue";
import SettlementRules from "@/components/PricingEngine/SettlementRules.vue";
import MarketData from "@/components/PricingEngine/MarketData.vue";
import ProductRules from "@/components/PricingEngine/ProductRules.vue";
import MarketPricesCalculation from "@/components/PricingEngine/MarketPricesCalculation.vue";
import CostAdditions from "@/components/PricingEngine/CostAdditions.vue";

import { getIceSymbol } from "@/util/calculations";
import { filterSettlementProducts } from "@/util/products";
import { rulesEnum } from "@/util/enums/rules";
import { costAdditionScope } from "@/util/enums/costAdditionScope";
import { createPricingRules, updateGlobalPricingRules } from "@/api/pricingRules";

export default {
  mixins: [breakpoint],
  components: {
    RefreshInformation,
    SettlementRules,
    MarketData,
    ProductRules,
    MarketPricesCalculation,
    CostAdditions,
  },
  data() {
    return {
      renderKey: 0,
      rules: {
        global: {},
        costAdditions: [],
      },
      componentMetadata: {
        settlement: {
          key: "SETTLEMENT",
          errorFound: false,
          rules: [],
          baseProducts: [],
        },
        market: {
          key: "MARKET",
          errorFound: false,
          rules: [],
        },
        product: {
          key: "PRODUCT",
          errorFound: false,
          rules: [],
        },
        calculation: {
          ice: {
            liveSymbol: "",
            settleSymbol: "",
            livePremium: 0,
          },
          products: [],
          savingStatus: {},
        },
        costAdditionsGlobal: {
          rules: [],
          unsavedChangesFound: false,
        },
        costAdditionsBio: {
          rules: [],
          unsavedChangesFound: false,
        },
        costAdditionsTemperature: {
          rules: [],
          unsavedChangesFound: false,
        },
        costAdditionsCustomer: {
          rules: [],
          unsavedChangesFound: false,
        },
      },
      navigation,
      selectedNav: {},
      targetNavId: "",
      displayCalculations: false,
      rulesEnum,
      costAdditionScopeGetter: costAdditionScope,
      nextFunction: null,
      currentSelectedTabInfo: null,
      pendingModificationRuleId: {},
      originalPricingRulesList: [],
    };
  },
  beforeRouteLeave(to, from, next) {
    if (this.getChangesDetected(this.selectedNav.id)) {
      this.showRemovalConfirmation(true);
      this.nextFunction = next;
    } else {
      next();
    }
  },
  created: async function () {
    await this.getMetadataConfig();
    if (!this.globalRulesGetter || Object.keys(this.globalRulesGetter).length === 0) {
      await this.getGlobalRules();
    }
    await this.populateComponentMetadata();
    this.selectedNav = this.navigation[0];
    this.populateCostAdditionRules();
  },
  computed: {
    ...mapGetters("orders", ["marketWindowId"]),
    ...mapGetters("metadata", ["productConfig"]),
    ...mapGetters("livePrices", ["getPricesUpdate"]),
    ...mapGetters("fuelsType", ["getFuelsType", "getSpotFuels"]),
    ...mapGetters("itemRemoval", ["unsavedChangesRemovalConfirmed", "showItemRemovalConfirmation"]),
    ...mapGetters("rules", [
      "globalRulesGetter",
      "costAdditionRulesGetter",
      "bioCostAdditionRulesGetter",
      "temperatureCostAdditionRulesGetter",
      "customerMarginRulesGetter",
    ]),
    ...mapGetters("rules", [
      "costAdditionRulesPagedGetter",
      "bioCostAdditionRulesPagedGetter",
      "temperatureCostAdditionRulesPagedGetter",
      "customerMarginRulesPagedGetter",
    ]),
    hasLivePrices() {
      return this.getPricesUpdate;
    },
    isFormCompletionPrevented: {
      get: function () {
        // If any of the components holds invalid rules, then prevent form completion
        for (let component in this.componentMetadata) {
          if (this.componentMetadata[component].errorFound) return true;
        }

        // Let's check whether user has changed any of the values yet or not
        if (this.rules.global && this.globalRulesGetter) {
          if (deepEqual(this.rules.global, this.globalRulesGetter)) return true;
        }

        return false;
      },
    },
  },
  watch: {
    marketWindowId(newId, oldId) {
      if (this.currentSelectedTabInfo && newId !== oldId) {
        this.populateCostAdditionRules(this.currentSelectedTabInfo.pageType, this.currentSelectedTabInfo);
      }
    },
    unsavedChangesRemovalConfirmed: {
      handler(isConfirmed) {
        if (isConfirmed) {
          // If user decides to not keep unsaved changes
          if (this.targetNavId != "") {
            this.selectNavItem(this.targetNavId, true); // Go to the selected tab
          } else if (this.nextFunction) {
            this.nextFunction();
          }
        } else {
          if (this.targetNavId === "" && this.nextFunction) {
            this.nextFunction(false);
          }
        }
        this.nextFunction = null;
      },
    },
    showItemRemovalConfirmation(newValue) {
      // we have to reset these values when closing the confirmation dialog else navigation gets confused
      if (newValue === false) {
        this.nextFunction = null;
        this.targetNavId = "";
      }
    },
  },
  methods: {
    ...mapActions("rules", ["getGlobalRules", "setCurrentTab"]),
    ...mapActions("metadata", ["getMetadataConfig"]),
    keepCurrentModifiedDataDisplayingToUser(ruleData, currentPricingData) {
      // if we have a pending modification we replace this data with anything new that might come from server
      if (!isEmpty(this.pendingModificationRuleId)) {
        if (!this.pendingModificationRuleId._id) {
          // new rules dont have id's and are automatically set to the start of the array
          ruleData.unshift(currentPricingData[0]);
        } else {
          let currentModifiedRuleIndex = currentPricingData.findIndex(
            (rule) => rule._id === this.pendingModificationRuleId._id
          );
          let newRuleDataSetRuleIndex = ruleData.findIndex((rule) => rule._id === this.pendingModificationRuleId._id);
          ruleData[newRuleDataSetRuleIndex] = cloneDeep(currentPricingData[currentModifiedRuleIndex]);
        }
        return ruleData;
      } else {
        return ruleData;
      }
    },
    async newLimitOrPageSelected(dto) {
      this.populateCostAdditionRules(dto.pageType, dto);
    },
    populateComponentMetadata: async function () {
      this.rules.global = JSON.parse(JSON.stringify(this.globalRulesGetter)); // Deep copy global rules to avoid altering values in the store

      /* ========= POPULATE SETTLEMENT METADATA ========= */
      this.componentMetadata.settlement.baseProducts = filterSettlementProducts(
        [
          // Set a list of base products in a particular order
          this.getFuelsType.find((f) => f._id === this.productConfig.DieselId),
          this.getFuelsType.find((f) => f._id === this.productConfig.KeroseneId),
          this.getFuelsType.find((f) => f._id === this.productConfig.BioDieselId),
          this.getFuelsType.find((f) => f._id === this.productConfig.GasolineId),
        ],
        this.getSpotFuels,
        this.productConfig
      );
      this.componentMetadata.settlement.rules = this.rules.global.dailySettlementRules;
      /* ================================================ */

      /* ========= POPULATE MARKET DATA METADATA ========= */
      this.componentMetadata.market.rules = this.rules.global.iceDataRules;
      /* ================================================= */

      /* ========= POPULATE PRODUCT METADATA ========= */
      this.componentMetadata.product.products = [
        // Set a list of non-blended products in a particular order
        this.getFuelsType.find((f) => f._id === this.productConfig.DieselId),
        this.getFuelsType.find((f) => f._id === this.productConfig.GasoilId),
        this.getFuelsType.find((f) => f._id === this.productConfig.KeroseneId),
        this.getFuelsType.find((f) => f._id === this.productConfig.BioDieselId),
        // adds it to the global product rules section on PE page
        this.getFuelsType.find((f) => f._id === this.productConfig.PetrolId),
        this.getFuelsType.find((f) => f._id === this.productConfig.PetrolWithAdditiveId),
        this.getFuelsType.find((f) => f._id === this.productConfig.SU97Id),
        this.getFuelsType.find((f) => f._id === this.productConfig.SU99Id),
        this.getFuelsType.find((f) => f._id === this.productConfig.DieselWithAdditiveId),
        this.getFuelsType.find((f) => f._id === this.productConfig.GasoilFame0Id),
        this.getFuelsType.find((f) => f._id === this.productConfig.HVOId),
        this.getFuelsType.find((f) => f._id === this.productConfig.MarineGasoilId),
        this.getFuelsType.find((f) => f._id === this.productConfig.HighBioMarineDieselId),
        this.getFuelsType.find((f) => f._id === this.productConfig.PremiumDieselId),
      ].filter((entry) => entry != null);
      this.componentMetadata.product.rules = [].concat.apply([], Object.values(this.rules.global.productRules));
      /* ============================================= */

      /* ========= POPULATE CALCULATION METADATA ========= */
      // Set a list of products for price calculation and order the products based on the steps of calculation
      const nonBlendedProductsIds = [
        this.productConfig.DieselId,
        this.productConfig.GasoilId,
        this.productConfig.KeroseneId,
        this.productConfig.BioDieselId,
        this.productConfig.BioDieselForGasoilId,
        this.productConfig.PetrolId,
        this.productConfig.PetrolWithAdditiveId,
        this.productConfig.SU97Id,
        this.productConfig.SU99Id,
        this.productConfig.DieselWithAdditiveId,
        this.productConfig.GasoilFame0Id,
        this.productConfig.HVOId,
        this.productConfig.MarineGasoilId,
        this.productConfig.HighBioMarineDieselId,
        this.productConfig.PremiumDieselId,
      ];
      const allProductsIds = [
        this.productConfig.DieselId,
        this.productConfig.BioDieselId,
        this.productConfig.BlendedDieselBioId,
        this.productConfig.GasoilId,
        this.productConfig.BlendedGasoilId,
        this.productConfig.KeroseneId,
        this.productConfig.BioDieselForGasoilId,
        this.productConfig.PetrolId,
        this.productConfig.PetrolWithAdditiveId,
        this.productConfig.SU97Id,
        this.productConfig.SU99Id,
        this.productConfig.DieselWithAdditiveId,
        this.productConfig.GasoilFame0Id,
        this.productConfig.HVOId,
        this.productConfig.MarineGasoilId,
        this.productConfig.HighBioMarineDieselId,
        this.productConfig.PremiumDieselId,
      ];
      const allProducts = this.getFuelsType
        .filter((f) => allProductsIds.includes(f._id))
        .map((p) => {
          return { ...p, isBlended: ![...nonBlendedProductsIds].includes(p._id) };
        });
      this.componentMetadata.calculation.products = [
        allProducts.find((f) => f._id === this.productConfig.DieselId),
        allProducts.find((f) => f._id === this.productConfig.GasoilId),
        allProducts.find((f) => f._id === this.productConfig.KeroseneId),
        allProducts.find((f) => f._id === this.productConfig.BioDieselForGasoilId),
        allProducts.find((f) => f._id === this.productConfig.BioDieselId),
        allProducts.find((f) => f._id === this.productConfig.BlendedGasoilId),
        allProducts.find((f) => f._id === this.productConfig.BlendedDieselBioId),
        allProducts.find((f) => f._id === this.productConfig.PetrolId),
        allProducts.find((f) => f._id === this.productConfig.PetrolWithAdditiveId),
        allProducts.find((f) => f._id === this.productConfig.SU97Id),
        allProducts.find((f) => f._id === this.productConfig.SU99Id),
        allProducts.find((f) => f._id === this.productConfig.DieselWithAdditiveId),
        allProducts.find((f) => f._id === this.productConfig.GasoilFame0Id),
        allProducts.find((f) => f._id === this.productConfig.HVOId),
        allProducts.find((f) => f._id === this.productConfig.MarineGasoilId),
        allProducts.find((f) => f._id === this.productConfig.HighBioMarineDieselId),
        allProducts.find((f) => f._id === this.productConfig.PremiumDieselId),
      ].filter((entry) => entry != null);
      const iceSettleMonth = this.rules.global.iceDataRules.find(
        (rule) => rule.action.targetAttribute === rulesEnum.eTargetAttributeType.IceSettle
      ).action.value;
      this.componentMetadata.calculation.ice.settleSymbol = getIceSymbol(iceSettleMonth);
      const iceLiveMonth = this.rules.global.iceDataRules.find(
        (rule) => rule.action.targetAttribute === rulesEnum.eTargetAttributeType.IceLive
      ).action.value;
      this.componentMetadata.calculation.ice.liveSymbol = getIceSymbol(iceLiveMonth);
      const iceLivePremium = this.rules.global.iceDataRules.find(
        (rule) => rule.action.targetAttribute === rulesEnum.eTargetAttributeType.IceLivePremium
      ).action.value;
      this.componentMetadata.calculation.ice.livePremium = Number(iceLivePremium);
      /* ================================================= */
    },
    setChangesDetected: function (scope, unsavedChangesFound) {
      switch (scope) {
        case "COST_ADDITIONS":
          this.componentMetadata.costAdditionsGlobal.unsavedChangesFound = unsavedChangesFound;
          break;
        case "BIO_COST_ADDITIONS":
          this.componentMetadata.costAdditionsBio.unsavedChangesFound = unsavedChangesFound;
          break;
        case "TEMPERATURE":
          this.componentMetadata.costAdditionsTemperature.unsavedChangesFound = unsavedChangesFound;
          break;
        case "CUSTOMER":
          this.componentMetadata.costAdditionsCustomer.unsavedChangesFound = unsavedChangesFound;
          break;
      }
    },
    getChangesDetected: function (navId) {
      let unsavedChangesFound = false;

      switch (navId) {
        case "GLOBAL":
          unsavedChangesFound = !deepEqual(this.rules.global, this.globalRulesGetter);
          break;
        case "COST_ADDITIONS":
          unsavedChangesFound = this.componentMetadata.costAdditionsGlobal.unsavedChangesFound;
          break;
        case "BIO_COST_ADDITIONS":
          unsavedChangesFound = this.componentMetadata.costAdditionsBio.unsavedChangesFound;
          break;
        case "TEMPERATURE":
          unsavedChangesFound = this.componentMetadata.costAdditionsTemperature.unsavedChangesFound;
          break;
        case "CUSTOMER":
          unsavedChangesFound = this.componentMetadata.costAdditionsCustomer.unsavedChangesFound;
          break;
      }
      return unsavedChangesFound;
    },
    selectNavItem(navId, isChangeConfirmed) {
      // this.selectedNav = this.navigation.find(n => n.id === navId) || this.navigation[0];
      if (navId && !isChangeConfirmed && navId !== this.selectedNav.id) {
        // Action is not confirmed, so check first whether we have unsaved changes or not
        if (this.getChangesDetected(this.selectedNav.id)) {
          this.targetNavId = navId;
          this.showRemovalConfirmation(true);
        } else {
          // No unsaved changges, so we can go to the selected tab
          this.selectNavItem(navId, true);
        }
      } else {
        // Action is confirmed, so go to the selected tab straight away
        if (navId !== this.selectedNav.id) {
          if (navId === "GLOBAL" || !navId) {
            this.populateComponentMetadata();
          } else {
            this.resetAllUnsavedChanges();
            this.setCurrentTab(navId);
            this.populateCostAdditionRules(navId);
          }
        }
        this.setChangesDetected(this.selectedNav.id, false);
        this.targetNavId = "";
        this.selectedNav = this.navigation.find((n) => n.id === navId) || this.navigation[0];
        this.showRemovalConfirmation(false);
      }
      this.displayCalculations = false; //set calculations view to false by default
    },
    showRemovalConfirmation: function (isOpening) {
      if (isOpening) {
        // Show removal confirmation modal
        const payload = {
          item: { priority: this.$store.getters["itemRemoval/eChangesType"].GlobalPricing },
          type: this.$store.getters["itemRemoval/eRemoveItemType"].UnconfirmedChanges,
          showWindow: true,
        };
        this.$store.dispatch("itemRemoval/openConfirmationRemoval", payload);
        setTimeout(() => {
          const removalModal = this.$root.$refs.confirmationRemoveItem.$el;
          $(removalModal).modal({ show: true, keyboard: false });
        }, 500);
      } else {
        // Close the removal confirmation modal and show the current modal
        this.$store.dispatch("itemRemoval/confirmUnsavedChangesRemoval", false);
      }
    },
    populateCostAdditionRulesPaged(dto) {
      this.populateCostAdditionRules(dto.pageType, dto);
    },
    formatValueToCorrectFormat(currentRuleData) {
      currentRuleData.forEach((element) => {
        element.action.value = this.$root.$options.filters.formatNumber(element.action.value);
      });
      this.originalPricingRulesList = cloneDeep(currentRuleData);
      return currentRuleData;
    },
    populateCostAdditionRules: async function (selectedTabId, dto = null) {
      const payload = {
        pageType: dto ? dto.pageType : "",
        pageLimit: dto ? dto.pageLimit : 25,
        pageNumber: dto ? dto.pageNumber : 1,
        priority: "GLOBAL",
      };
      this.currentSelectedTabInfo = payload;
      /**
       *  1. Unless tab ID is specified, fetch all cost addition rules and re-assign objects in the store,
       *  2. Make a deep copy of each cost addition type,
       *  3. Map target objects in the child component
       **/
      switch (selectedTabId) {
        case "GLOBAL":
          break;
        case "COST_ADDITIONS": {
          // eslint-disable-next-line no-case-declarations
          payload.pageType = dto ? dto.pageType : selectedTabId;
          await this.$store.dispatch("rules/getGlobalSpecifiedRulesPaged", payload);
          let currentRuleData = cloneDeep(this.costAdditionRulesPagedGetter);
          currentRuleData = this.formatValueToCorrectFormat(currentRuleData);
          this.componentMetadata.costAdditionsCustomer.rules = [];
          this.componentMetadata.costAdditionsTemperature.rules = [];
          this.componentMetadata.costAdditionsBio.rules = [];
          this.componentMetadata.costAdditionsGlobal.rules = this.keepCurrentModifiedDataDisplayingToUser(
            currentRuleData,
            this.componentMetadata.costAdditionsGlobal.rules
          );
          break;
        }
        case "BIO_COST_ADDITIONS": {
          // eslint-disable-next-line no-case-declarations
          payload.pageType = dto ? dto.pageType : selectedTabId;
          await this.$store.dispatch("rules/getGlobalSpecifiedRulesPaged", payload);
          let currentRuleData = cloneDeep(this.bioCostAdditionRulesPagedGetter);
          currentRuleData = this.formatValueToCorrectFormat(currentRuleData);
          this.componentMetadata.costAdditionsCustomer.rules = [];
          this.componentMetadata.costAdditionsTemperature.rules = [];
          this.componentMetadata.costAdditionsGlobal.rules = [];
          this.componentMetadata.costAdditionsBio.rules = this.keepCurrentModifiedDataDisplayingToUser(
            currentRuleData,
            this.componentMetadata.costAdditionsBio.rules
          );
          break;
        }
        case "TEMPERATURE": {
          // eslint-disable-next-line no-case-declarations
          payload.pageType = dto ? dto.pageType : selectedTabId;
          await this.$store.dispatch("rules/getGlobalSpecifiedRulesPaged", payload);
          let currentRuleData = cloneDeep(this.temperatureCostAdditionRulesPagedGetter);
          currentRuleData = this.formatValueToCorrectFormat(currentRuleData);
          this.componentMetadata.costAdditionsCustomer.rules = [];
          this.componentMetadata.costAdditionsBio.rules = [];
          this.componentMetadata.costAdditionsGlobal.rules = [];
          this.componentMetadata.costAdditionsTemperature.rules = this.keepCurrentModifiedDataDisplayingToUser(
            currentRuleData,
            this.componentMetadata.costAdditionsTemperature.rules
          );
          break;
        }
        case "CUSTOMER": {
          // eslint-disable-next-line no-case-declarations
          payload.pageType = dto ? dto.pageType : selectedTabId;
          await this.$store.dispatch("rules/getGlobalSpecifiedRulesPaged", payload);
          let currentRuleData = cloneDeep(this.customerMarginRulesPagedGetter);
          currentRuleData = this.formatValueToCorrectFormat(currentRuleData);
          this.componentMetadata.costAdditionsTemperature.rules = [];
          this.componentMetadata.costAdditionsBio.rules = [];
          this.componentMetadata.costAdditionsGlobal.rules = [];
          this.componentMetadata.costAdditionsCustomer.rules = this.keepCurrentModifiedDataDisplayingToUser(
            currentRuleData,
            this.componentMetadata.costAdditionsCustomer.rules
          );
          break;
        }
        default:
          break;
      }
    },
    assignIceData: async function (targetAttribute, symbolOrValue) {
      const eTargetAttributeType = rulesEnum.eTargetAttributeType;
      if (targetAttribute == eTargetAttributeType.IceSettle) {
        // Fetch selected ICE settlement
        this.componentMetadata.calculation.ice.settleSymbol = symbolOrValue;
      } else if (targetAttribute == eTargetAttributeType.IceLive) {
        // Assign selected ICE live symbol to fetch the correct live price from market window
        this.componentMetadata.calculation.ice.liveSymbol = symbolOrValue;
      } else if (targetAttribute == eTargetAttributeType.IceLivePremium) {
        // Assign ICE live premium for calculation
        this.componentMetadata.calculation.ice.livePremium = symbolOrValue;
      }
    },
    preventFormCompletion: function (componentKey, isErrorFound) {
      const component = this.componentMetadata[componentKey.toLowerCase()];
      if (component) component.errorFound = isErrorFound;
    },
    saveGlobalPricingRules: async function () {
      let allRules = JSON.parse(
        JSON.stringify([
          ...this.componentMetadata.settlement.rules,
          ...this.componentMetadata.market.rules,
          ...this.componentMetadata.product.rules,
        ])
      );

      // Let's ensure that action values are of type Number
      for (let rule of allRules) {
        const { value } = rule.action;
        rule.action.value = Number(value);
      }

      const newRules = allRules.filter((r) => !r._id);
      const updatedRules = allRules
        .filter((r) => r._id)
        .map((r) => {
          return { ...r, ruleId: r._id };
        });

      try {
        if (newRules.length > 0) {
          // Create new rules
          await createPricingRules(newRules, { settleTasOrders: true });
        }

        if (updatedRules.length > 0) {
          // Update existing rules
          await updateGlobalPricingRules(updatedRules, { settleTasOrders: true });
        }

        // Re-fetch and re-populate using updated rules
        this.updateGlobalRulesSavingStatus({ rulesSaved: true, message: "Rules saved" });
        await this.getGlobalRules();
        this.populateComponentMetadata();

        // Let's now update the "Market Prices" table and list of offers
        const updatedPrices = {
          marketWindowId: this.$store.getters["orders/marketWindowId"],
          dailySettlement: this.rules.global.dailySettlementRules,
        };
        this.$store.dispatch("livePrices/getSellerProductPrices", updatedPrices);
        this.$store.dispatch("sellerOffers/getAllOffersForUser");
      } catch (ex) {
        this.updateGlobalRulesSavingStatus({
          rulesSaved: false,
          message: "Unable to save rules. Please try again later",
        }); // Flag to tell users that request failed
      }
    },
    updateGlobalRulesSavingStatus: function (savingStatus) {
      this.componentMetadata.calculation.savingStatus = savingStatus;
    },
    toggleDisplayCalculations: function () {
      this.displayCalculations = !this.displayCalculations;
    },
    resetAllUnsavedChanges() {
      this.componentMetadata.costAdditionsGlobal.unsavedChangesFound = false;
      this.componentMetadata.costAdditionsBio.unsavedChangesFound = false;
      this.componentMetadata.costAdditionsTemperature.unsavedChangesFound = false;
      this.componentMetadata.costAdditionsCustomer.unsavedChangesFound = false;
    },
    updateRenderKey() {
      this.renderKey++;
    },
  },
};
const navigation = [
  {
    id: "GLOBAL",
    title: "Global Pricing Rules",
    descriptionBeforeCalcs: [
      "Global Pricing Rules will be applied across all of your offers.",
      "Market change (ppl) is calculated per product.",
    ],
    calculations: [
      "Settle Base Price (ppl) = (Product Settlement USD/mt / GBPUSD Settlement / Product Density * 100) + Product Duty",
      "Market Change (ppl) = ((LS Gasoil Future + Product Differential) / Product Density * 100 / GBPUSD Live Exchange) - (Product Settlement / Product Density * 100 / GBPUSD Settlement))",
      "Product Differential = Product Settlement - LS Gasoil Future Settlement",
      "Live Base Price (ppl) = Settle Base Price (ppl) + Market Change (ppl)",
    ],
    descriptionAfterCalcs: [
      "*Blended Gasoil and Blended Diesel market change calculations take into account the RTFO bio percentage split that can be managed within this tab",
    ],
    scope: "",
    pricingRules: "rules",
  },
  {
    id: "COST_ADDITIONS",
    title: "Cost Addition Rules",
    descriptionBeforeCalcs: [
      "Add your cost breakdowns below. These rules will be applied when you create your offers.",
      "Price rules entered in USD/mt are converted back into ppl using the live exchange rate and relevant product density.",
    ],
    calculations: ["Calculated Price rule (ppl) = Price rule in USD/mt / GBPUSD Live Exchange / Product Density *100"],
    descriptionAfterCalcs: ["Product Density can be adjusted within the Global Pricing Rules tab"],
    scope: costAdditionScope.Global,
  },
  {
    id: "BIO_COST_ADDITIONS",
    title: "Bio Rules",
    descriptionBeforeCalcs: [
      "Add your Bio Rules below. These rules are applied 100 per cent to Split Quote offers. The Settle and Live Base Prices in ppl for a split quote offer are calculated using the RTFO bio percentage split managed within the Global Pricing Rules tab.",
      "Price rules entered in USD/mt are converted back into ppl using the live exchange rate and relevant product density.",
    ],
    calculations: [
      "Calculated Price rule (ppl) = Price rule in USD/mt / GBPUSD Live Exchange / Biodiesel Density * 100 * RTFO bio percentage",
      "Calculated Price rule (ppl) = Price rule in ppl * RTFO bio percentage",
    ],
    descriptionAfterCalcs: ["Product Density can be adjusted within the Global Pricing Rules tab"],
    scope: costAdditionScope.Bio,
  },
  {
    id: "TEMPERATURE",
    title: "Temperature Rules",
    descriptionBeforeCalcs: [
      "Add your temperature rules below. These will only be applied to any ambient offers that you create.",
      "You can either add margins or discounts yourself or you can input physical temperatures and the pricing engine will calculate the standard temperature adjustment from degrees celsius to ppl.",
    ],
    calculations: [
      "Calculation 1: STA (USD/mt) = ((STA correction Factor -1) * Product Duty * Product Density * GBPUSD Settlement / 100) + ((LS Gasoil Future + Product Differential) * (STA correction factor -1)) ",
      "Calculation 2: STA (ppl) = Calculation 1 / GBPUSD Settlement / Product Density *100",
    ],
    descriptionAfterCalcs: ["STA correction factors are taken from government published figures."],
    scope: costAdditionScope.Temperature,
  },
  {
    id: "CUSTOMER",
    title: "Customer Margins",
    descriptionBeforeCalcs: [
      "Add your customer margins below. These rules can be applied per customer or to multiple customers for different products and terminals.",
      "Price rules entered in USD/mt are converted back into ppl using the live exchange rate and relevant product density.",
    ],
    calculations: ["Calculated Price rule (ppl) = Price rule in USD/mt / GBPUSD Live Exchange / Product Density *100"],
    descriptionAfterCalcs: ["Product Density can be adjusted within the Global Pricing Rules tab"],
    scope: costAdditionScope.Customer,
  },
];
</script>

<style lang="scss" scoped>
@import "@/assets/scss/components/_modals.scss";

.top-shadow {
  -webkit-filter: drop-shadow(0px 0px 10px rgba(map-get($platform-theme, pe-sticky-shadow), 0.9));
  filter: drop-shadow(0px 0px 10px rgba(map-get($platform-theme, pe-sticky-shadow), 0.9));
}
</style>
